import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { zodResolver } from "@hookform/resolvers/zod"
import { Info, SpinnerGap, Warning } from "@phosphor-icons/react"
import { Button } from "@/components/ui/button"
import { DatePicker, VISUAL_DATE_PLACEHOLDER } from "@/components/ui/date-picker"
import { Form, FormField } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"
import { useToast } from "@/hooks/useToast"
import { DateOnly } from "@/lib/date-only"
import { LookupResultCard } from "@/modules/admin/sasis-lookup/components/lookup-result-card"
import { ViewModeToggle } from "@/modules/admin/sasis-lookup/components/view-mode-toggle"
import { describeLookupFailure } from "@/modules/admin/sasis-lookup/describe-lookup-failure"
import {
  healthInsuranceCardNumberPattern,
  keepDigitsOnly,
  SasisLookupSchema,
  sasisLookupSchema,
} from "@/modules/admin/sasis-lookup/schema/sasis-lookup-schema"
import {
  fetchInsureeDataFromSasisByHealthInsuranceCardNumber,
  InsureeDataFromSasis,
} from "@/modules/admin/sasis-lookup/services/insureeDataFromSasisByHealthInsuranceCardNumberQuery"
import { fetchInsureeDataFromSasisByInsureeNumber } from "@/modules/admin/sasis-lookup/services/insureeDataFromSasisByInsureeNumberQuery"
import { fetchInsureeDataFromSasisByNameAndDateOfBirth } from "@/modules/admin/sasis-lookup/services/insureeDataFromSasisByNameAndDateOfBirthQuery"
import {
  fetchInsureeDataFromSasisBySocialSecurityNumber,
  InsureeDataFromSasisAtEveryInsurer,
} from "@/modules/admin/sasis-lookup/services/insureeDataFromSasisBySocialSecurityNumberQuery"
import { LOOKUP_MODES, LookupResultEntry, ViewMode } from "@/modules/admin/sasis-lookup/types/lookup-result-entry"

const SasisLookup = () => {
  const { t } = useTranslation()
  const { toast } = useToast()

  const form = useForm<SasisLookupSchema>({
    resolver: zodResolver(sasisLookupSchema),
    mode: "onChange",
    defaultValues: {
      mode: "veka",
      healthInsuranceCardNumber: "",
      nationalInsuranceNumber: "",
      insureeNumber: "",
      firstName: "",
      lastName: "",
      dateOfBirth: undefined,
      socialSecurityNumber: "",
    },
  })

  const { control, handleSubmit, setValue, watch, formState } = form
  const mode = watch("mode")
  const healthInsuranceCardNumber = watch("healthInsuranceCardNumber")

  const [viewMode, setViewMode] = useState<ViewMode>("mapped")
  const [singleInsureeResult, setSingleInsureeResult] = useState<InsureeDataFromSasis | null>(null)
  const [socialSecurityNumberResult, setSocialSecurityNumberResult] =
    useState<InsureeDataFromSasisAtEveryInsurer | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lastSearchedCardNumber, setLastSearchedCardNumber] = useState<string | null>(null)

  const clearResults = () => {
    setSingleInsureeResult(null)
    setSocialSecurityNumberResult(null)
    setError(null)
    setLastSearchedCardNumber(null)
  }

  const handleModeChange = (value: string) => {
    const nextMode = LOOKUP_MODES.find((lookupMode) => lookupMode === value)
    if (!nextMode) return

    setValue("mode", nextMode, { shouldValidate: true })
    clearResults()
  }

  const runSearch = (search: () => Promise<void>) => {
    setIsLoading(true)
    setError(null)

    search()
      .catch((searchError: Error) => setError(searchError.message))
      .finally(() => setIsLoading(false))
  }

  const submitSearch = (values: SasisLookupSchema) => {
    switch (values.mode) {
      case "veka":
        setLastSearchedCardNumber(values.healthInsuranceCardNumber)
        runSearch(async () => {
          setSingleInsureeResult(
            await fetchInsureeDataFromSasisByHealthInsuranceCardNumber(values.healthInsuranceCardNumber),
          )
        })
        return

      case "insureeNumber":
        runSearch(async () => {
          setSingleInsureeResult(
            await fetchInsureeDataFromSasisByInsureeNumber(
              Number(values.nationalInsuranceNumber.trim()),
              values.insureeNumber.trim(),
            ),
          )
        })
        return

      case "nameAndDateOfBirth": {
        const { dateOfBirth } = values
        if (dateOfBirth === undefined) return

        runSearch(async () => {
          setSingleInsureeResult(
            await fetchInsureeDataFromSasisByNameAndDateOfBirth(
              values.firstName.trim(),
              values.lastName.trim(),
              new DateOnly(dateOfBirth).toString(),
            ),
          )
        })
        return
      }

      case "ahv":
        runSearch(async () => {
          setSocialSecurityNumberResult(null)
          setSocialSecurityNumberResult(
            await fetchInsureeDataFromSasisBySocialSecurityNumber(keepDigitsOnly(values.socialSecurityNumber)),
          )
        })
        return
    }
  }

  // The card number search is one cheap request, so it runs as soon as the number is complete.
  useEffect(() => {
    if (mode !== "veka") return
    if (!healthInsuranceCardNumberPattern.test(healthInsuranceCardNumber)) return
    if (healthInsuranceCardNumber === lastSearchedCardNumber) return

    setLastSearchedCardNumber(healthInsuranceCardNumber)
    setSingleInsureeResult(null)
    setIsLoading(true)
    setError(null)

    fetchInsureeDataFromSasisByHealthInsuranceCardNumber(healthInsuranceCardNumber)
      .then(setSingleInsureeResult)
      .catch((searchError: Error) => setError(searchError.message))
      .finally(() => setIsLoading(false))
  }, [mode, healthInsuranceCardNumber, lastSearchedCardNumber])

  const entries: ReadonlyArray<LookupResultEntry> =
    mode === "ahv"
      ? (socialSecurityNumberResult?.organisationResults ?? []).map((organisationResult) => ({
          entryKey: String(organisationResult.nationalInsuranceNumber),
          organisationName: organisationResult.organisationName,
          mapped: organisationResult.result?.mapped ?? null,
          rawXml: organisationResult.result?.rawXml ?? null,
          returnCode: organisationResult.result?.returnCode ?? null,
          returnMessage: organisationResult.result?.returnMessage ?? null,
          errorMessage: organisationResult.errorMessage,
        }))
      : singleInsureeResult?.rawXml || singleInsureeResult?.mapped
        ? [
            {
              entryKey: mode,
              organisationName: null,
              mapped: singleInsureeResult.mapped,
              rawXml: singleInsureeResult.rawXml,
              returnCode: singleInsureeResult.returnCode,
              returnMessage: singleInsureeResult.returnMessage,
              errorMessage: null,
            },
          ]
        : []

  const searchedInsurerCount = entries.filter((entry) => entry.mapped).length
  const unsearchableInsurerCount = entries.length - searchedInsurerCount

  const returnCodeMessage =
    mode !== "ahv" && singleInsureeResult && !singleInsureeResult.mapped && !singleInsureeResult.rawXml
      ? describeLookupFailure(t, singleInsureeResult.returnCode, singleInsureeResult.returnMessage, null)
      : null

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text)
    toast({ description: t("admin.sasisLookup.copied") })
  }

  const searchButton = (testId: string) => (
    <Button type="submit" disabled={!formState.isValid || isLoading} data-testid={testId}>
      {isLoading ? <SpinnerGap className="size-4 animate-spin" /> : null}
      {t("admin.sasisLookup.search")}
    </Button>
  )

  return (
    <div className="flex h-full flex-col gap-6 overflow-auto p-6">
      <h1 className="text-2xl font-semibold">{t("admin.sasisLookup.pageTitle")}</h1>

      <ToggleGroup type="single" value={mode} onValueChange={handleModeChange} data-testid="sasis-mode-toggle">
        {LOOKUP_MODES.map((lookupMode) => (
          <ToggleGroupItem key={lookupMode} value={lookupMode} data-testid={`sasis-mode-${lookupMode}`}>
            {t(`admin.sasisLookup.${lookupMode}.modeLabel`)}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>

      {mode === "ahv" ? (
        <div className="bg-warning-surface text-on-warning-surface flex items-center gap-3 rounded-md p-3">
          <Warning className="size-5 shrink-0" weight="fill" />
          <p className="text-sm">{t("admin.sasisLookup.ahv.cautionMessage")}</p>
        </div>
      ) : null}

      {mode === "nameAndDateOfBirth" ? (
        <div className="bg-surface-subdued text-on-surface flex items-center gap-3 rounded-md p-3">
          <Info className="size-5 shrink-0" />
          <p className="text-sm">{t("admin.sasisLookup.nameAndDateOfBirth.privacyMessage")}</p>
        </div>
      ) : null}

      <Form {...form}>
        <form onSubmit={handleSubmit(submitSearch)} className="flex flex-wrap items-start gap-4">
          {mode === "veka" ? (
            <div className="flex max-w-md flex-1 flex-col">
              <FormField
                control={control}
                name="healthInsuranceCardNumber"
                render={({ field, fieldState }) => (
                  <Input
                    label={t("admin.sasisLookup.veka.inputPlaceholder")}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    assistiveText={t("admin.sasisLookup.veka.validationHint")}
                    error={fieldState.error?.message}
                    translateError
                    isLoading={isLoading}
                    testId="veka-input"
                  />
                )}
              />
            </div>
          ) : null}

          {mode === "insureeNumber" ? (
            <>
              <div className="flex max-w-xs flex-1 flex-col">
                <FormField
                  control={control}
                  name="nationalInsuranceNumber"
                  render={({ field, fieldState }) => (
                    <Input
                      label={t("admin.sasisLookup.insureeNumber.nationalInsuranceNumberLabel")}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      error={fieldState.error?.message}
                      translateError
                      testId="national-insurance-number-input"
                    />
                  )}
                />
              </div>
              <div className="flex max-w-xs flex-1 flex-col">
                <FormField
                  control={control}
                  name="insureeNumber"
                  render={({ field, fieldState }) => (
                    <Input
                      label={t("admin.sasisLookup.insureeNumber.inputPlaceholder")}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      error={fieldState.error?.message}
                      translateError
                      testId="insuree-number-input"
                    />
                  )}
                />
              </div>
              {searchButton("insuree-number-search-button")}
            </>
          ) : null}

          {mode === "nameAndDateOfBirth" ? (
            <>
              <div className="flex max-w-xs flex-1 flex-col">
                <FormField
                  control={control}
                  name="firstName"
                  render={({ field, fieldState }) => (
                    <Input
                      label={t("admin.sasisLookup.nameAndDateOfBirth.firstNameLabel")}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      error={fieldState.error?.message}
                      translateError
                      testId="first-name-input"
                    />
                  )}
                />
              </div>
              <div className="flex max-w-xs flex-1 flex-col">
                <FormField
                  control={control}
                  name="lastName"
                  render={({ field, fieldState }) => (
                    <Input
                      label={t("admin.sasisLookup.nameAndDateOfBirth.lastNameLabel")}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      error={fieldState.error?.message}
                      translateError
                      testId="last-name-input"
                    />
                  )}
                />
              </div>
              <div className="flex max-w-xs flex-1 flex-col">
                <FormField
                  control={control}
                  name="dateOfBirth"
                  render={({ field, fieldState }) => (
                    <DatePicker
                      label={t("admin.sasisLookup.nameAndDateOfBirth.dateOfBirthLabel")}
                      value={field.value}
                      onSelect={field.onChange}
                      onReset={() => field.onChange(undefined)}
                      readOnly={false}
                      placeholder={VISUAL_DATE_PLACEHOLDER}
                      error={fieldState.error?.message}
                      translateError
                      dataTestId="date-of-birth-input"
                    />
                  )}
                />
              </div>
              {searchButton("name-and-date-of-birth-search-button")}
            </>
          ) : null}

          {mode === "ahv" ? (
            <>
              <div className="flex max-w-md flex-1 flex-col">
                <FormField
                  control={control}
                  name="socialSecurityNumber"
                  render={({ field, fieldState }) => (
                    <Input
                      label={t("admin.sasisLookup.ahv.inputPlaceholder")}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      assistiveText={t("admin.sasisLookup.ahv.validationHint")}
                      error={fieldState.error?.message}
                      translateError
                      testId="ahv-input"
                    />
                  )}
                />
              </div>
              {searchButton("social-security-number-search-button")}
            </>
          ) : null}

          <ViewModeToggle viewMode={viewMode} onViewModeChange={setViewMode} />
        </form>
      </Form>

      {isLoading && mode === "ahv" ? (
        <div className="text-muted-foreground">{t("admin.sasisLookup.ahv.loading")}</div>
      ) : null}

      {error ? <div className="text-destructive">{error}</div> : null}

      {returnCodeMessage && !isLoading ? (
        <div className="text-muted-foreground" data-testid="sasis-return-code-message">
          {returnCodeMessage}
        </div>
      ) : null}

      {mode === "ahv" && socialSecurityNumberResult && !isLoading ? (
        <div className="text-muted-foreground flex flex-col gap-1" data-testid="sasis-ahv-summary">
          <span>{t("admin.sasisLookup.ahv.organisationCount", { count: searchedInsurerCount })}</span>
          {unsearchableInsurerCount > 0 ? (
            <span className="text-destructive">
              {t("admin.sasisLookup.ahv.unsearchableCount", { count: unsearchableInsurerCount })}
            </span>
          ) : null}
        </div>
      ) : null}

      {entries.length > 0 && !isLoading ? (
        <div className="flex flex-col gap-4" data-testid="sasis-results">
          {entries.map((entry) => (
            <LookupResultCard key={entry.entryKey} entry={entry} viewMode={viewMode} onCopy={handleCopy} />
          ))}
        </div>
      ) : null}
    </div>
  )
}

export { SasisLookup }
